import { useEffect, useRef, useState } from "react"

import { MotionValue, PanInfo, useVelocity } from "framer-motion"

import { WebApp } from "@/shared/telegram"

interface DragBehaviorProps {
  /** MotionValue tracking the Y position */
  y: MotionValue<number>
  /** Whether the modal is open */
  isOpen: boolean
  /** Function to call to close the modal */
  onClose: () => void
  /** Optional function to trigger before closing */
  onBeforeClose?: () => boolean
  /** Reference to the modal element */
  modalRef: React.RefObject<HTMLDivElement | null>
  /** Reference to the content element */
  contentRef: React.RefObject<HTMLDivElement | null>
  /** Whether haptic feedback is disabled */
  disableHaptics?: boolean
}

export interface DragBehaviorState {
  /** Whether the user is currently dragging */
  isDragging: boolean
  /** Whether drag has passed the dismissal threshold */
  isPastThreshold: boolean
  /** Progress of the drag from 0 to 1 */
  dragProgress: number
  /** Whether interaction is in the content area */
  isContentInteraction: boolean
  /** Whether content is being scrolled */
  isScrollingContent: boolean
}

export interface DragBehaviorHandlers {
  /** Handler for drag start */
  onDragStart: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void
  /** Handler for drag movement */
  onDrag: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void
  /** Handler for drag end */
  onDragEnd: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void
  /** Handler for content pointer down */
  handleContentPointerDown: () => void
  /** Handler for content touch start */
  handleContentTouchStart: (e: React.TouchEvent) => void
  /** Handler for content touch move */
  handleContentTouchMove: (e: React.TouchEvent) => void
  /** Handler for content touch end */
  handleContentTouchEnd: () => void
  /** Sets content interaction state */
  setIsContentInteraction: (value: boolean) => void
}

/**
 * Custom hook to handle drag behavior for bottom sheet modals
 */
export const useDragBehavior = ({
  y,
  isOpen,
  onClose,
  onBeforeClose,
  modalRef,
  contentRef,
  disableHaptics = false
}: DragBehaviorProps): [DragBehaviorState, DragBehaviorHandlers] => {
  const { impactOccurred } = WebApp.HapticFeedback
  const velocity = useVelocity(y)

  // State for tracking interactions
  const [isDragging, setIsDragging] = useState(false)
  const [isPastThreshold, setIsPastThreshold] = useState(false)
  const [dragProgress, setDragProgress] = useState(0)
  const [isContentInteraction, setIsContentInteraction] = useState(false)
  const [isScrollingContent, setIsScrollingContent] = useState(false)

  // Refs for smoother interactions
  const lastFeedbackTimeRef = useRef<number>(0)
  const lastHapticDirectionRef = useRef<"up" | "down" | null>(null)
  const startDragYRef = useRef<number>(0)
  const isScrollAtTopRef = useRef<boolean>(true)
  const [touchStartY, setTouchStartY] = useState<number | null>(null)

  // Close handler with optional confirmation
  const handleClose = () => {
    if (onBeforeClose && !onBeforeClose()) {
      const currentY = y.get()
      y.set(currentY)
      setTimeout(() => {
        y.set(0)
      }, 0)
      return
    }

    if (!disableHaptics) {
      impactOccurred("light")
    }
    onClose()
  }

  // Check if content is scrolled to top - helps with better gesture handling
  useEffect(() => {
    const checkScroll = () => {
      if (contentRef.current) {
        isScrollAtTopRef.current = contentRef.current.scrollTop <= 0
      }
    }

    const contentEl = contentRef.current
    if (contentEl) {
      contentEl.addEventListener("scroll", checkScroll)
      // Initialize on mount
      checkScroll()
    }

    return () => {
      contentEl?.removeEventListener("scroll", checkScroll)
    }
  }, [isOpen, contentRef])

  // Update dragProgress based on y position changes
  useEffect(() => {
    const unsubscribe = y.on("change", latestY => {
      if (!isDragging) {
        // When not dragging (like in animations), calculate the dragProgress
        // based on the current y position
        const modalHeight = modalRef.current?.offsetHeight || 0
        if (modalHeight) {
          const calculatedProgress = Math.min(Math.max(latestY, 0) / (modalHeight * 0.4), 1)
          setDragProgress(calculatedProgress)
        }
      }
    })

    return unsubscribe
  }, [y, isDragging, modalRef])

  // Handle drag start
  const onDragStart = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    setIsDragging(true)
    setIsPastThreshold(false)
    startDragYRef.current = info.point.y

    // Reset the progress at the start of each drag
    setDragProgress(0)

    // Only provide haptic feedback when dragging from the header
    // Check if the target is within the header
    const target = event.target as HTMLElement
    const isHeaderDrag = target?.closest("header") !== null

    if (!disableHaptics && isHeaderDrag) {
      impactOccurred("light")
    }
  }

  // Handle the actual dragging with progressive resistance
  const onDrag = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    // Skip handling if we're actively scrolling content
    if (isScrollingContent) {
      y.set(0)
      return
    }

    // Get modal height to calculate thresholds
    const modalHeight = modalRef.current?.offsetHeight || 0

    // Check if we're trying to drag up (negative y)
    if (info.offset.y < 0) {
      // Only allow upward drag if we're not at the top of the content
      if (!isScrollAtTopRef.current) {
        // Don't move the modal, let the content scroll
        y.set(0)
        setDragProgress(0)
        return
      } else {
        // If we're at the top, allow slight elastic upward movement with strong resistance
        const resistedY = info.offset.y * 0.1 // Strong resistance for upward movement
        const limitedY = Math.max(resistedY, -20) // Limit upward movement to 20px
        y.set(limitedY)
        // For upward drag, we still want a consistent 0 for visual feedback
        setDragProgress(0)
        return
      }
    }

    // Progressive resistance - more resistance as the user drags further
    const dragDistance = info.offset.y
    const dragPercentage = Math.min(dragDistance / (modalHeight * 0.4), 1)

    // Apply non-linear resistance factor that increases with drag distance
    // This gives a more iOS-like feel where resistance increases the more you pull
    const resistance = 1 - Math.pow(dragPercentage, 1.5) * 0.6
    const adjustedY = dragDistance * resistance

    // Update the motion value
    y.set(adjustedY)

    // Update drag progress for visual feedback
    setDragProgress(dragPercentage)

    // Determine haptic feedback thresholds
    const dismissThreshold = modalHeight * 0.15
    const firstFeedbackThreshold = modalHeight * 0.05
    const secondFeedbackThreshold = modalHeight * 0.1

    // Current position and status
    const position = adjustedY
    const now = Date.now()
    const hapticCooldown = 300
    const timeSinceLastFeedback = now - lastFeedbackTimeRef.current

    // Multi-stage haptic feedback logic - similar to iOS
    let shouldProvideFeedback = false
    let direction: "up" | "down" | null = null
    let intensity: "light" | "medium" | "heavy" = "light"

    // First feedback threshold - hint that gesture is recognized
    if (!isPastThreshold && position > firstFeedbackThreshold) {
      shouldProvideFeedback = true
      direction = "down"
      intensity = "light"
      setIsPastThreshold(true)
    }
    // Second feedback threshold - stronger hint that release will dismiss
    else if (isPastThreshold && position > secondFeedbackThreshold && position <= dismissThreshold) {
      shouldProvideFeedback = true
      direction = "down"
      intensity = "medium"
    }
    // Going back up, reversing the threshold crossing
    else if (isPastThreshold && position < firstFeedbackThreshold) {
      shouldProvideFeedback = true
      direction = "up"
      intensity = "light"
      setIsPastThreshold(false)
    }

    // Only provide feedback if enough time has passed and direction changed
    // AND we're not in a content interaction
    if (
      shouldProvideFeedback &&
      timeSinceLastFeedback > hapticCooldown &&
      lastHapticDirectionRef.current !== direction &&
      !disableHaptics &&
      !isContentInteraction
    ) {
      lastFeedbackTimeRef.current = now
      lastHapticDirectionRef.current = direction
      impactOccurred(intensity)
    }
  }

  // Handle drag end with improved velocity detection
  const onDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    setIsDragging(false)
    setIsPastThreshold(false)
    lastHapticDirectionRef.current = null

    // Reset drag progress after animation completes
    setTimeout(() => {
      setDragProgress(0)
    }, 300)

    // Get modal height to calculate threshold
    const modalHeight = modalRef.current?.offsetHeight || 0
    const dismissThreshold = modalHeight * 0.15

    // Velocity-based dismissal - iOS-like behavior where flick gestures work
    const currentVelocity = velocity.get()
    const velocityThreshold = 500 // px/s, adjust based on feel

    // Close if dragged beyond threshold OR velocity is high enough
    if (info.offset.y > dismissThreshold || currentVelocity > velocityThreshold) {
      if (!disableHaptics && !isContentInteraction) {
        impactOccurred("medium")
      }

      // For velocity-based dismissals, add a small delay to make the animation visible
      if (currentVelocity > velocityThreshold && info.offset.y < dismissThreshold) {
        // Quick animation showing the sheet moving down before closing
        y.set(dismissThreshold * 1.2)
        setTimeout(handleClose, 120)
      } else {
        handleClose()
      }
    } else {
      // Create a spring-like animation back to position
      // This matches iOS feel better than an instant snap
      const currentY = y.get()
      y.set(currentY)
      setTimeout(() => {
        y.set(0)
      }, 0)

      // Provide subtle feedback when returning to position
      if (!disableHaptics && y.get() > 20 && !isContentInteraction) {
        // Only if we've dragged a meaningful amount and not in content area
        impactOccurred("light")
      }
    }
  }

  // Handle content interaction detection with improved scroll behavior
  const handleContentPointerDown = () => {
    setIsContentInteraction(true)

    // Check if we're at top of scroll to allow drag if needed
    if (contentRef.current) {
      isScrollAtTopRef.current = contentRef.current.scrollTop <= 0
    }
  }

  // Touch event handlers for content area
  const handleContentTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation()
    setTouchStartY(e.touches[0].clientY)
    setIsScrollingContent(false)
  }

  const handleContentTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation()
    if (touchStartY !== null) {
      const deltaY = touchStartY - e.touches[0].clientY
      // If we're scrolling content, mark it
      if (Math.abs(deltaY) > 5) {
        setIsScrollingContent(true)
      }
    }
  }

  const handleContentTouchEnd = () => {
    setTouchStartY(null)
    // Reset after a short delay to allow other interactions
    setTimeout(() => {
      setIsScrollingContent(false)
    }, 300)
  }

  const state: DragBehaviorState = {
    isDragging,
    isPastThreshold,
    dragProgress,
    isContentInteraction,
    isScrollingContent
  }

  const handlers: DragBehaviorHandlers = {
    onDragStart,
    onDrag,
    onDragEnd,
    handleContentPointerDown,
    handleContentTouchStart,
    handleContentTouchMove,
    handleContentTouchEnd,
    setIsContentInteraction
  }

  return [state, handlers]
}

export default useDragBehavior
